import { useState } from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';
import type { AlertData } from './AlertCard';

interface AcknowledgeModalProps {
  alert: AlertData;
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
  isLoading?: boolean;
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Critical Alert',
    labelColor: 'text-red-700',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'Warning',
    labelColor: 'text-amber-700',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'Info',
    labelColor: 'text-blue-700',
  },
};

export function AcknowledgeModal({
  alert,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: AcknowledgeModalProps) {
  const [notes, setNotes] = useState('');
  const config = severityConfig[alert.severity];
  const Icon = config.icon;

  const handleConfirm = () => {
    onConfirm(notes.trim() || undefined);
    setNotes('');
  };

  const handleClose = () => {
    if (!isLoading) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Acknowledge Alert" size="md">
      <div className="space-y-5">
        {/* Alert Summary */}
        <div
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg border',
            config.bgColor,
            config.borderColor
          )}
        >
          <div className="shrink-0 mt-0.5">
            <Icon className={cn('w-5 h-5', config.iconColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-gray-900">{alert.pond_name || 'Unknown Pond'}</span>
              <span className={cn('text-xs font-medium', config.labelColor)}>
                {config.label}
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{alert.message}</p>
            {alert.triggered_value !== undefined && alert.threshold_value !== undefined && (
              <p className="mt-1.5 text-xs text-gray-500">
                Value: <span className="font-mono font-medium text-gray-700">{alert.triggered_value}</span>
                {' '}(threshold: {alert.threshold_value})
              </p>
            )}
          </div>
        </div>

        {/* Notes Input */}
        <Textarea
          label="Notes (optional)"
          placeholder="Add any notes about how this alert was addressed..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          helperText="Notes will be saved with the acknowledgment for future reference."
        />

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            leftIcon={<CheckCircle2 className="w-4 h-4" />}
          >
            Acknowledge
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
